import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { PageBody } from '../components/layout/PageBody';
import { ShellCard } from '../components/layout/ShellCard';
import { shellSx } from '../theme/tokens';
import { ArticleCard } from '../components/feed/ArticleCard';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { LoadingState } from '../components/common/LoadingState';
import { useFeedback } from '../components/common/FeedbackProvider';
import { useAuth } from '../hooks/useAuth';
import { useInfiniteFeed } from '../hooks/useInfiniteFeed';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { shareContent } from '../utils/share';

const FEED_LIMIT = 20;

export function FeedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const q = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(q);
  const [busyId, setBusyId] = useState(null);
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  function writeSearch(nextQ) {
    const next = {};
    if (nextQ) next.q = nextQ;
    setSearchParams(next, { replace: true });
  }

  useEffect(() => {
    const next = debouncedSearch.trim();
    if (next === q) return;
    writeSearch(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    const message = location.state?.authMessage;
    if (!message) return;
    showError(message);
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate, showError]);

  const filters = useMemo(
    () => ({
      q: q || undefined,
    }),
    [q],
  );

  const {
    items,
    loading,
    error,
    setError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    reload,
    react,
    recordShare,
  } = useInfiniteFeed(FEED_LIMIT, filters);

  const sentinelRef = useInfiniteScroll({
    enabled: !loading && !error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const featured = items[0];
  const rest = items.slice(1);

  function onSearchSubmit(e) {
    e.preventDefault();
    writeSearch(searchInput.trim());
  }

  async function onReact(article, emoji) {
    if (!user) {
      navigate('/login', {
        state: {
          from: `${location.pathname}${location.search}`,
          authMessage: 'Sign in to like stories',
        },
      });
      return;
    }
    setBusyId(article.id);
    try {
      await react(article, emoji);
      showSuccess(emoji ? 'Liked' : 'Like removed');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Reaction failed');
    } finally {
      setBusyId(null);
    }
  }

  async function onShare(article) {
    try {
      const result = await shareContent({
        title: article.title || 'Prasaar',
        text: article.summary || '',
        url:
          article.url ||
          `${window.location.origin}/article/${article.id}`,
      });
      if (result === 'cancelled') return;
      if (user) {
        try {
          await recordShare(article);
        } catch {
          /* share still succeeded locally */
        }
      }
      showSuccess(result === 'shared' ? 'Shared' : 'Link copied');
    } catch {
      showError('Could not share');
    }
  }

  return (
    <AppShell title="Feed" subtitle="Broadcast stories from Prasaar">
      <PageBody>
        <ShellCard>
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            <Box
              component="form"
              onSubmit={onSearchSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                size="medium"
                label="Search stories"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexShrink: 0 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  sx={{ color: '#fff', minWidth: 88, flexShrink: 0 }}
                >
                  Search
                </Button>
                {q && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearchInput('');
                      writeSearch('');
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </ShellCard>

        {loading && <LoadingState label="Loading feed…" />}
        <ErrorAlert message={error} onClose={() => setError(null)} />

        {error && !loading && (
          <Button variant="outlined" onClick={() => void reload()}>
            Retry
          </Button>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState
            title={q ? `No stories match “${q}”` : 'No published stories yet'}
            description={
              q
                ? 'Try a different search, or show all stories.'
                : 'Check back soon for curated Prasaar broadcasts.'
            }
            action={
              q ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchInput('');
                    writeSearch('');
                  }}
                >
                  Show all
                </Button>
              ) : null
            }
          />
        )}

        {(featured || rest.length > 0) && (
          <Stack spacing={{ xs: 1.5, sm: 2 }}>
            {featured && (
              <ArticleCard
                article={featured}
                featured={!q}
                index={0}
                onReact={onReact}
                onShare={onShare}
                busy={busyId === featured.id}
              />
            )}
            {rest.map((article, i) => (
              <ArticleCard
                key={article.id}
                article={article}
                index={i + 1}
                onReact={onReact}
                onShare={onShare}
                busy={busyId === article.id}
              />
            ))}
          </Stack>
        )}

        {!loading && !error && items.length > 0 && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            {isFetchingNextPage && (
              <Typography variant="body2" sx={shellSx.textMuted}>
                Loading more…
              </Typography>
            )}
            {!hasNextPage && !isFetchingNextPage && (
              <Typography variant="body2" sx={shellSx.textMuted}>
                That&apos;s all for now
              </Typography>
            )}
          </Box>
        )}

        <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden />
      </PageBody>
    </AppShell>
  );
}
